<template>
  <Teleport to="body">
    <Transition name="lg-drawer">
      <div
        v-if="open"
        class="lg-drawer-scrim"
        @pointerdown.self="$emit('close')"
        @keydown.esc="$emit('close')"
      >
        <aside
          ref="panel"
          class="lg-drawer"
          :class="`side-${side}`"
          role="dialog"
          aria-modal="true"
          :aria-label="ariaLabel ?? title"
          tabindex="-1"
          @keydown="trap.onKeydown"
        >
          <header v-if="title || $slots.header" class="lg-drawer-head">
            <slot name="header">
              <span class="lg-drawer-title">{{ title }}</span>
            </slot>
            <button
              class="lg-drawer-close"
              type="button"
              aria-label="Close"
              @click="$emit('close')"
            >
              <X :size="18" aria-hidden="true" />
            </button>
          </header>
          <div class="lg-drawer-body">
            <slot />
          </div>
        </aside>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, watch, nextTick } from 'vue';
import { X } from 'lucide-vue-next';
import { useFocusTrap } from './useFocusTrap.js';
import { useScrollLock } from './useScrollLock.js';

const props = withDefaults(
  defineProps<{
    open: boolean;
    side?: 'left' | 'right';
    title?: string;
    ariaLabel?: string;
  }>(),
  { side: 'left' },
);

defineEmits<{ close: [] }>();

const panel = ref<HTMLElement | null>(null);
const trap = useFocusTrap(panel);

// Ref-counted body scroll lock — shared with LgModal so that overlapping
// overlays (e.g. two drawers, or a modal opened over a drawer) don't clobber
// each other's restore. Unmount-while-open is handled inside the composable.
const scrollLock = useScrollLock();

watch(
  () => props.open,
  (isOpen) => {
    if (isOpen) {
      scrollLock.lock();
      nextTick(() => trap.focusFirst());
    } else {
      scrollLock.unlock();
    }
  },
  { immediate: true },
);
</script>

<style scoped>
.lg-drawer-scrim {
  position: fixed;
  inset: 0;
  z-index: var(--lg-z-drawer);
  display: flex;
  background: var(--lg-scrim);
}
.side-right {
  margin-left: auto;
}
.lg-drawer {
  display: flex;
  flex-direction: column;
  width: min(84vw, 320px);
  height: 100%;
  background: var(--lg-bg-raised);
  box-shadow: var(--lg-shadow-3);
}
.side-left {
  border-right: 1px solid var(--lg-border);
}
.side-right {
  border-left: 1px solid var(--lg-border);
}
.lg-drawer:focus {
  outline: none;
}
.lg-drawer-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--lg-space-3);
  min-height: var(--lg-topbar-h);
  padding: 0 var(--lg-space-3) 0 var(--lg-space-4);
  border-bottom: 1px solid var(--lg-border-faint);
}
.lg-drawer-title {
  font-size: var(--lg-fs-md);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--lg-text-dim);
}
.lg-drawer-close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: var(--lg-touch);
  height: var(--lg-touch);
  border: none;
  border-radius: var(--lg-radius-md);
  background: transparent;
  color: var(--lg-text-dim);
  cursor: pointer;
}
.lg-drawer-close:hover {
  color: var(--lg-text);
  background: var(--lg-bg-hover);
}
.lg-drawer-body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
}

/* transition */
.lg-drawer-enter-active,
.lg-drawer-leave-active {
  transition: opacity var(--lg-dur-base) var(--lg-ease);
}
.lg-drawer-enter-active .lg-drawer,
.lg-drawer-leave-active .lg-drawer {
  transition: transform var(--lg-dur-base) var(--lg-ease);
}
.lg-drawer-enter-from,
.lg-drawer-leave-to {
  opacity: 0;
}
.lg-drawer-enter-from .side-left,
.lg-drawer-leave-to .side-left {
  transform: translateX(-100%);
}
.lg-drawer-enter-from .side-right,
.lg-drawer-leave-to .side-right {
  transform: translateX(100%);
}
</style>
